import React from 'react';
import { motion } from 'framer-motion';
import { useModal } from '../services/ModalService';
import '../styles/pages/BookkeepingAccounting.css';

const BookkeepingAccounting = () => {
  const { openContactModal } = useModal();

  return (
    <div className="bookkeepingaccounting">
      {/* Hero Section */}
      <section className="bookkeeping-hero">
        <div className="bookkeeping-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bookkeeping-hero-content"
          >
            <div className="hero-badge">
              📊 Scale Your CPA Practice
            </div>
            
            <h1 className="hero-title"
            dangerouslySetInnerHTML={{ __html: "White-Label <span class=\"gradient-text\">Bookkeeping & Accounting</span><br />Services for CPA Firms" }}
            />
            
            <p className="hero-subtitle">
              Expand your firm's capacity with our comprehensive bookkeeping and accounting services. Delivered seamlessly under your brand from our India and US operations, enabling you to serve more clients at 60% lower costs while maintaining exceptional quality.
            </p>
            
            <div className="hero-buttons">
              <button 
                className="btn-primary"
                onClick={() => openContactModal(
                  'Bookkeeping & Accounting Services',
                  'Bookkeeping Page - Schedule Firm Consultation'
                )}
              >
                Schedule Firm Consultation
              </button>
              <button 
                className="btn-secondary"
                onClick={() => openContactModal(
                  'Cost Savings Analysis',
                  'Bookkeeping Page - View Cost Savings'
                )}
              >
                View Cost Savings
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bookkeeping-section">
        <div className="bookkeeping-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="bookkeeping-section-title gradient-text">White-Label Bookkeeping Services</h2>
            <p className="bookkeeping-section-subtitle">
              Complete bookkeeping solutions delivered under your firm's brand with full client confidentiality
            </p>
            
            <div className="bookkeeping-services-grid">
              
              <div className="bookkeeping-glass-card">
                <h3>Daily Transaction Processing</h3>
                <p>Complete daily bookkeeping with real-time processing using QuickBooks, Xero, Wave, and other leading platforms your clients prefer.</p>
                <ul>
                  <li>✓ Multi-platform expertise (QuickBooks, Xero, Wave, FreshBooks)</li>
                  <li>✓ Same-day transaction recording</li>
                  <li>✓ Bank & credit card reconciliations</li>
                  <li>✓ Accounts payable/receivable management</li>
                </ul>
              </div>

              <div className="bookkeeping-glass-card">
                <h3>Client-Ready Financial Statements</h3>
                <p>Professional financial statements prepared under your firm's letterhead, ready for immediate client delivery and review.</p>
                <ul>
                  <li>✓ Monthly/quarterly financial packages</li>
                  <li>✓ Custom reporting templates</li>
                  <li>✓ Management dashboard creation</li>
                  <li>✓ Variance analysis and insights</li>
                </ul>
              </div>

              <div className="bookkeeping-glass-card">
                <h3>Month-End & Year-End Support</h3>
                <p>Comprehensive period-end procedures ensuring your clients' books are audit-ready and compliant with accounting standards.</p>
                <ul>
                  <li>✓ Adjusting journal entries</li>
                  <li>✓ Trial balance preparation</li>
                  <li>✓ Fixed asset management</li>
                  <li>✓ Pre-audit workpaper preparation</li>
                </ul>
              </div>

              <div className="bookkeeping-glass-card">
                <h3>Specialized Industry Expertise</h3>
                <p>Deep expertise across various industries including construction, professional services, retail, and technology companies.</p>
                <ul>
                  <li>✓ Construction WIP & job costing</li>
                  <li>✓ Professional services time & billing</li>
                  <li>✓ E-commerce multi-channel accounting</li>
                  <li>✓ SaaS revenue recognition</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Technology & Tools Section */}
      <section className="bookkeeping-section">
        <div className="bookkeeping-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h2 className="bookkeeping-section-title gradient-text">
              Technology Expertise & Cost Advantages
            </h2>
            
            <div className="bookkeeping-tech-grid">
              
              <div className="bookkeeping-tech-card">
                <div className="bookkeeping-tech-icon">🛠️</div>
                <h4 className="bookkeeping-tech-title">Platform Mastery</h4>
                <p className="bookkeeping-tech-description">Certified experts in all major accounting platforms</p>
                <div className="bookkeeping-tech-details">
                  QuickBooks • Xero • Wave • FreshBooks • Sage • NetSuite • Peachtree
                </div>
              </div>

              <div className="bookkeeping-tech-card">
                <div className="bookkeeping-tech-icon">💰</div>
                <h4 className="bookkeeping-tech-title">60% Cost Savings</h4>
                <p className="bookkeeping-tech-description">India-US delivery model reduces operational costs significantly</p>
                <div className="bookkeeping-tech-details">
                  Pass savings to clients or increase margins
                </div>
              </div>

              <div className="bookkeeping-tech-card">
                <div className="bookkeeping-tech-icon">⏰</div>
                <h4 className="bookkeeping-tech-title">24/7 Operations</h4>
                <p className="bookkeeping-tech-description">Round-the-clock processing across time zones</p>
                <div className="bookkeeping-tech-details">
                  Next-day delivery standard
                </div>
              </div>

              <div className="bookkeeping-tech-card">
                <div className="bookkeeping-tech-icon">🎯</div>
                <h4 className="bookkeeping-tech-title">White-Label Delivery</h4>
                <p className="bookkeeping-tech-description">All work delivered under your firm's brand</p>
                <div className="bookkeeping-tech-details">
                  Your clients never know we exist
                </div>
              </div>

              <div className="bookkeeping-tech-card">
                <div className="bookkeeping-tech-icon">📈</div>
                <h4 className="bookkeeping-tech-title">Scalable Growth</h4>
                <p className="bookkeeping-tech-description">Handle 3x more clients without hiring</p>
                <div className="bookkeeping-tech-details">
                  Flexible capacity scaling
                </div>
              </div>

              <div className="bookkeeping-tech-card">
                <div className="bookkeeping-tech-icon">🛡️</div>
                <h4 className="bookkeeping-tech-title">Quality Assurance</h4>
                <p className="bookkeeping-tech-description">Multi-level review process ensures accuracy</p>
                <div className="bookkeeping-tech-details">
                  99.8% accuracy guarantee
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bookkeeping-section">
        <div className="bookkeeping-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="bookkeeping-cta-card">
              <h3 className="bookkeeping-cta-title">
                Ready to Scale Your Practice?
              </h3>
              <p className="bookkeeping-cta-subtitle">
                Join 25+ CPA firms who have transformed their practice with our white-label bookkeeping services. Focus on advisory work while we handle the data entry and reconciliations.
              </p>
              <div className="bookkeeping-cta-buttons">
                <button 
                  className="btn-primary"
                  onClick={() => openContactModal(
                    'Strategy Call - Bookkeeping Services',
                    'Bookkeeping Page - Schedule Strategy Call'
                  )}
                >
                  Schedule Strategy Call
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => openContactModal(
                    'Case Studies Request',
                    'Bookkeeping Page - Download Case Studies'
                  )}
                >
                  Download Case Studies
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BookkeepingAccounting;